import { Admin } from "@/models/admin";
import {ModuleService} from "@/services/module-service";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import * as firebaseAuth from "firebase/auth";
import Vue from "vue";
import { ServerConfig } from "../config/config";
import { AuthSocketService } from "./auth-socket-service";
import { MainSocketService } from "./main-socket-service";
import { TokenStore } from "@/utils/token-store";
import { ERR_TOKEN_EXPIRED } from "@/utils/socket-service-factory";
import { HttpError } from "@/utils/http-error";
import { AdminsService } from "./admins-service";
import { TicketService } from "./ticket-service";
import { ExtraButtonService } from "./extra-button-service";

export const Axios = axios.create();

export const AuthService = new Vue({
	data() {
		return {
			ready: false,
			isLoggedIn: false,
			isRefreshingTokens: false,
			user: null as Admin | null
		};
	},
	computed: {
		baseUrl():string {
			return ServerConfig.host + "/admin/auth";
		},
	},
	methods: {
		async login(token:string) {
			let postResult = await axios.post(this.baseUrl + "/login", {token});
			TokenStore.accessToken = postResult.data.token;
			TokenStore.userId = postResult.data.id;
			this.user = new Admin(postResult.data.user);
			TokenStore.email = this.user.email;
			TokenStore.accessExpires = new Date(postResult.data.expires * 1000);
			TokenStore.refreshToken = postResult.data.refreshToken;
			TokenStore.refreshExpires = new Date(postResult.data.expires * 1000);
			await this.initAfterLogin();
			AuthSocketService.connect();
			MainSocketService.connect();
		},
		async logout() {
			firebaseAuth.signOut(firebaseAuth.getAuth());
			try {
				await axios.post(this.baseUrl + "/logout", {}, {headers: {Authorization: TokenStore.refreshToken}});
			} catch(err) {
				let error = err as AxiosError;
				if (error.code == "0") {
					throw err;
				}
			}
			this.isLoggedIn = false;
			TokenStore.accessToken = null;
			TokenStore.email = null;
			TokenStore.userId = null;
			TokenStore.accessExpires = null;
			TokenStore.refreshToken = null;
			TokenStore.refreshExpires = null;
			AuthSocketService.disconnect();
			MainSocketService.disconnect();
		},
		async refreshToken() {
			if (this.isRefreshingTokens){
				return new Promise<void>((resolve, reject)=>{
					let failHandler:Function;
					let successHandler = ()=>{
						this.$off("token-refresh-failed", failHandler);
						resolve();
					};
					failHandler = ()=>{
						this.$off("token-refreshed", successHandler);
						reject();
					};
					this.$once("token-refreshed", successHandler);
					this.$once("token-refresh-failed", failHandler);
				});
			}
			try{
				this.isRefreshingTokens = true;
				let result = await axios.post(this.baseUrl + "/refresh-token", {}, {headers: {
					Authorization: TokenStore.refreshToken
				}});
				TokenStore.accessToken = result.data.token;
				TokenStore.accessExpires = new Date(result.data.expires * 1000);
				TokenStore.refreshToken = result.data.refreshToken;
				TokenStore.refreshExpires = new Date(result.data.refreshTokenExpires * 1000);
				this.user = new Admin(result.data.user);
				await AuthSocketService.connect();
				await MainSocketService.connect();
				this.isLoggedIn = true;
			}catch(err){
				if ((err as AxiosError).response){
					this.handleErrorResponse((err as AxiosError).response as AxiosResponse);
				}
				this.$emit("token-refresh-failed");
			}
			this.isRefreshingTokens = false;
		},
		async initAfterLogin() {
			await ModuleService.getModules();
			await AdminsService.getAdmins();
			await TicketService.init();
			await ExtraButtonService.init();
			this.isLoggedIn = true;
		},
		initSocketEvents(){
			MainSocketService.$on(ERR_TOKEN_EXPIRED, async ()=>{
				await this.refreshToken();
			});
			AuthSocketService.$on(ERR_TOKEN_EXPIRED, async ()=>{
				await this.refreshToken();
			});
		},
		handleErrorResponse(response:AxiosResponse){
			if (typeof(response.data) == "object"){
				let err = new HttpError(response.data);
				requestAnimationFrame(()=>{
					if (err.dontShow) return;
					this.$emit("request-error", err.getMessage());
				});
				if (err.key == "TOKEN_EXPIRED"){
					this.logout();
				}
				throw(err);
			}else{
				this.$emit("request-error", `${response.status}: ${response.data || "Server error"}`);
			}
		},
		initAxiosInterceptors(){
			Axios.interceptors.request.use(async(value:AxiosRequestConfig):Promise<AxiosRequestConfig> => {
				if (TokenStore.accessExpires) {
					if(TokenStore.accessExpires.getTime() < new Date().getTime()) {
						await this.refreshToken();
					}
				}
				value.headers.Authorization = TokenStore.accessToken;
				return value;
			});

			Axios.interceptors.response.use(undefined, async(value:AxiosError):Promise<AxiosError | AxiosError> => {
				if (!value.response) {
					this.$emit("request-error", "000: Server not responding!");
				} else if (value.response) {
					this.handleErrorResponse(value.response);
				}
				throw value;
			});
		}
	},
	async created() {
		this.initSocketEvents();
		this.initAxiosInterceptors();
		TicketService.registerSocketEvents();

		try{
			if (TokenStore.refreshToken) {
				await this.refreshToken();
				this.initAfterLogin();
			}
		}catch(err){
			console.error(err);
		}
		this.ready = true;
	}
});

